import {
  AreaPage,
  DiscardDialog,
  hashRouteOf,
  hasUnsavedChanges,
  mountApp,
  NotFoundState,
  PageNotice,
  PROFILE_GROUPS,
  ProfileArea,
  ProfileOverview,
  profilePageOf,
  setProblemTexts,
  SignInGate,
  type Me,
  type NavSection,
} from '@kanos/web-core';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { AnnouncementsPage } from './announcements';
import { ContractFieldsPage } from './contract-fields';
import { FaqPage } from './faq';
import { MaintenancePage } from './maintenance';
import { PartnerAppsPage } from './partner-apps';
import { PlatformShell } from './shell';
import { StaffPage } from './staff';
import { FirmsPage } from './firms';
import { TasksPage } from './tasks';
import './styles.css';

// On the platform, rights are granted by the Kanos team, not by a firm.
setProblemTexts({
  'access.denied':
    'Dafür fehlt Ihnen die Berechtigung. Plattformrechte vergibt das Kanos-Team (ADR-016).',
});

// `/firms` is the page `#/firms`, before anything reads the address (the sign-in's way back too).
const hashRoute = hashRouteOf(window.location);
if (hashRoute !== null) window.history.replaceState(null, '', hashRoute);

const SECTIONS: readonly NavSection[] = [
  { base: '/profile', title: 'Profil', groups: PROFILE_GROUPS },
];

/** The page of the address' hash (`#/team`); the start without one. */
function useHashPath(): { path: string; guard: ReactNode } {
  const read = () => window.location.hash.replace(/^#/, '') || '/';
  const [path, setPath] = useState(read);
  // A way out while something is unsaved waits here until the person decides.
  const [pending, setPending] = useState<string | null>(null);
  const shown = useRef(path);
  const leaving = useRef(false);
  useEffect(() => {
    const follow = () => {
      const next = read();
      if (next === shown.current) return;
      if (hasUnsavedChanges() && !leaving.current) {
        // The address shows the page on screen again; the hash cannot be held back.
        window.history.replaceState(null, '', `#${shown.current}`);
        setPending(next);
        return;
      }
      leaving.current = false;
      shown.current = next;
      setPath(next);
      document.getElementById('main-content')?.focus({ preventScroll: true });
      window.scrollTo(0, 0);
    };
    window.addEventListener('hashchange', follow);
    return () => window.removeEventListener('hashchange', follow);
  }, []);
  const guard =
    pending === null ? null : (
      <DiscardDialog
        onKeep={() => setPending(null)}
        onDiscard={() => {
          setPending(null);
          leaving.current = true;
          window.location.hash = pending;
        }}
      />
    );
  return { path, guard };
}

/** The page of a path: the platform's work, or the own profile with its areas and tabs. */
function PlatformPage({ me, path }: { me: Me; path: string }) {
  if (path === '/') {
    return (
      <AreaPage title="Partner-Apps">
        <PartnerAppsPage me={me} />
      </AreaPage>
    );
  }
  if (path === '/help') {
    return (
      <AreaPage title="Häufige Fragen">
        <FaqPage />
      </AreaPage>
    );
  }
  if (path === '/contract-fields') {
    return (
      <AreaPage title="Vertragsangaben">
        <ContractFieldsPage />
      </AreaPage>
    );
  }
  if (path === '/firms') {
    return (
      <AreaPage title="Kanzleien">
        <FirmsPage />
      </AreaPage>
    );
  }
  if (path === '/tasks') {
    return (
      <AreaPage title="Aufgaben">
        <TasksPage />
      </AreaPage>
    );
  }
  if (path === '/maintenance') {
    return (
      <AreaPage title="Wartung">
        <MaintenancePage />
      </AreaPage>
    );
  }
  if (path === '/announcements') {
    return (
      <AreaPage title="Neuigkeiten">
        <AnnouncementsPage />
      </AreaPage>
    );
  }
  if (path === '/team') {
    return (
      <AreaPage title="Plattform-Team">
        <StaffPage />
      </AreaPage>
    );
  }
  if (path === '/profile') {
    return (
      <ProfileOverview
        me={me}
        link={(area, content, className) => (
          <a href={`#/profile/${area}`} className={className}>
            {content}
          </a>
        )}
      />
    );
  }
  const page = path.startsWith('/profile/') ? profilePageOf(path.slice('/profile/'.length)) : null;
  if (!page) return <NotFoundState onHome={() => window.location.assign('#/')} />;
  return (
    <ProfileArea
      me={me}
      area={page.area}
      tab={page.tab}
      go={(tab) => window.location.assign(securityHash(tab))}
      link={(tab, content, className, current) => (
        <a
          href={securityHash(tab)}
          aria-current={current ? 'page' : undefined}
          className={className}
        >
          {content}
        </a>
      )}
    />
  );
}

/** A tab of „Anmeldung & Sicherheit“ in the platform's hash routes. */
const securityHash = (tab: string) => (tab ? `#/profile/security/${tab}` : '#/profile/security');

function PlatformApp({ me }: { me: Me }) {
  const { path, guard } = useHashPath();
  return (
    <PlatformShell me={me} pathname={path} sections={SECTIONS}>
      <PlatformPage me={me} path={path} />
      {guard}
    </PlatformShell>
  );
}

mountApp(
  <SignInGate>
    {(me) =>
      // The API decides; this only keeps a session of another app from a dead end.
      me.app === 'platform' ? (
        <PlatformApp me={me} />
      ) : (
        <PageNotice title="Diese Anmeldung gilt nicht für die Plattform" />
      )
    }
  </SignInGate>,
);
